import React, { Dispatch, SetStateAction } from 'react';
import {
  Box,
  Checkbox,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from '@chakra-ui/react';
import Card from 'layouts/Card';
import { useTranslation } from 'react-i18next';
import { BasketParams } from 'queries/baskets/baskets.types';
import { MonitoringProposalOverview } from 'queries/monitoring';
import { ShowProductSnapshotModalInfo } from 'components/Product/Product.ModalInfo';

const BasketSimple = ({
  basket,
  proposalsOverview,
  setSelectedProposal,
  selectedProposal,
  ...props
}: {
  basket: BasketParams;
  proposalsOverview: MonitoringProposalOverview[];
  setSelectedProposal: Dispatch<SetStateAction<number | null>>;
  selectedProposal: number | null;
  [key: string]: unknown;
}): JSX.Element => {
  const { t } = useTranslation();
  const {
    id: basketId,
    publisher: { name: publisherName },
  } = basket;

  const proposals = React.useMemo(
    () => proposalsOverview.filter(({ basket: id }) => id === basketId),
    [proposalsOverview, basketId],
  );

  const proposalsWithoutCodes = React.useMemo(
    () => proposals.filter(({ codes }) => codes.length === 0).length,
    [proposals],
  );

  const toggleProposalSelected = React.useCallback(
    (proposalId: number) => {
      setSelectedProposal(prev => (prev === proposalId ? null : proposalId));
    },
    [setSelectedProposal],
  );

  return (
    <Card padding={4} size='sm' variant='unstyled' {...props}>
      <Accordion allowToggle defaultIndex={[0]} variant='wpp'>
        <AccordionItem>
          <AccordionButton>
            <Text
              as='h4'
              size='md'
              flex='1'
              textAlign='left'
              display='flex'
              alignItems='center'
            >
              {t(
                'basket.labelWithPublisherNameHeaderSimple',
                'Koszyk {{publisherName}} (Transakcje bez kodów: {{count}})',
                { publisherName, count: proposalsWithoutCodes },
              )}
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Accordion allowToggle defaultIndex={[0]} variant='gray'>
              <AccordionItem>
                <AccordionButton>
                  <Box
                    flex='1'
                    textAlign='left'
                    display='flex'
                    alignItems='center'
                  >
                    <Box fontWeight='bold' fontSize='md' letterSpacing='wide'>
                      {t('proposal_other', 'Produkty zlecenia')}
                    </Box>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <Table variant='simple' size='sm' fontSize='sm'>
                    <Thead>
                      <Tr>
                        <Th />
                        <Th>{t('monitoring.codeShort', 'Kod')}</Th>
                        <Th>ID</Th>
                        <Th>{t('product_one', 'Produkt')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {proposals.map(
                        ({
                          id: proposalId,
                          product: { name },
                          codes,
                        }: MonitoringProposalOverview) => {
                          const codesCount = codes.length;
                          return (
                            <Tr key={proposalId}>
                              <Td width='1%'>
                                <Checkbox
                                  variant='ghost'
                                  isChecked={selectedProposal === proposalId}
                                  isDisabled={
                                    selectedProposal !== null &&
                                    selectedProposal !== proposalId
                                  }
                                  onChange={() =>
                                    toggleProposalSelected(proposalId)
                                  }
                                />
                              </Td>
                              <Td
                                color={
                                  codesCount === 0
                                    ? 'notification.alarm.bg'
                                    : undefined
                                }
                              >
                                {codesCount}
                              </Td>
                              <Td>#{proposalId}</Td>
                              <Td>
                                <ShowProductSnapshotModalInfo
                                  proposalId={proposalId}
                                />
                              </Td>
                            </Tr>
                          );
                        },
                      )}
                    </Tbody>
                  </Table>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

BasketSimple.displayName = 'BasketSimple';
export { BasketSimple };
